import React, { useState, useEffect, useCallback } from 'react'
import { Box, Image } from '@chatterbug/aaron'
import { IconButton } from '@chatterbug/theme'
import PropTypes from 'prop-types'
import { t } from 'src/lib/i18n-react'

import { useProductTour } from 'src/lib/product-tour-context'
import close from 'src/ui/assets/icons/close.svg'

const ALREADY_SEEN_IN_THIS_SESSION = {}
const closeButton = <Image src={close} alt={t('Dismiss')} />

const NudgeCard = ({ children, name }) => {
  const [cardState, setCardState] = useState('open')
  const context = useProductTour(name)
  const [visible, setVisible] = useState(false)

  const handleSeen = useCallback(() => {
    if (ALREADY_SEEN_IN_THIS_SESSION[name]) {
      return
    }
    // keep a cache inside the component to avoid rerenderings
    // if we would have updated it in the context
    ALREADY_SEEN_IN_THIS_SESSION[name] = true
    context?.onSeen?.(name)
  }, [context, name])

  const handleClose = () => {
    setCardState('closing')
  }

  useEffect(() => {
    const isVisible = !ALREADY_SEEN_IN_THIS_SESSION[name] && context?.enabled
    if (isVisible && visible !== isVisible) {
      setVisible(isVisible)
    }
  }, [visible, setVisible, context, name])

  useEffect(() => {
    let timeout
    if (cardState === 'closing') {
      timeout = setTimeout(() => setCardState('closed'), 300)
    }

    return () => window.clearTimeout(timeout)
  }, [cardState, setCardState])

  useEffect(() => {
    if (cardState === 'closed') {
      handleSeen()
      setVisible(false)
    }
  }, [cardState, handleSeen, setVisible])

  if (!visible) {
    return null
  }

  return (
    <Box
      boxShadow="raised"
      borderRadius="1x"
      py={{
        _: '3x',
        tablet: '5x',
      }}
      px={{
        _: '3x',
        tablet: '5x',
      }}
      position="relative"
      overflow="hidden"
      bg="white100"
      sx={{
        opacity: ['closing', 'closed'].includes(cardState) ? 0 : 1,
        transition: cardState === 'closing' ? 'opacity 0.2s ease-out' : '',
      }}
    >
      <Box position="absolute" right={2} top={2}>
        <IconButton
          as="submit"
          kind="tertiary"
          icon={closeButton}
          data-testid="close-button"
          onClick={handleClose}
        />
      </Box>
      {children}
    </Box>
  )
}

NudgeCard.propTypes = {
  name: PropTypes.string.isRequired,
}

export default NudgeCard
